import { z } from 'zod';
import { APP_BADGE_MAX } from '../attention.js';
import { DecisionValue, Id, InterventionStatus, MessageAuthorType, MessageKind, Timestamp } from './common.js';

/**
 * Vague 5 : messagerie centrale de l'espace garage, notifications push et lecture de plaque.
 */

/** Booléen de query string (`?unread=true`) : `true`/`1` ou `false`/`0`. */
export const queryBool = z.preprocess(
  (v) => (v === 'true' || v === '1' ? true : v === 'false' || v === '0' ? false : v),
  z.boolean(),
);

/** Fenêtre des dossiers clôturés listés avec `scope=all` (jours). */
export const INBOX_CLOSED_WINDOW_DAYS = 30;
/** Longueur de l'extrait du dernier message. */
export const INBOX_EXCERPT_LENGTH = 120;

/**
 * `inbox.list`.
 * - `scope` : `open` (dossiers non clôturés, défaut) ou `all` (plus les dossiers clôturés depuis
 *   `INBOX_CLOSED_WINDOW_DAYS` jours au plus) ;
 * - `unread` : au moins un message non lu du client pour **ce membre** ;
 * - `toHandle` : au moins un message du client non traité (`handledAt` nul) ;
 * - `mine` : dossiers assignés au membre (`assignedMembershipId`) ;
 * - `pinned` (03/10/2026) : dossiers épinglés par **ce membre** ;
 * - `q` : plaque (forme compacte), nom du client, marque ou modèle (sans casse ni accents) ;
 * - `cursor` : curseur opaque (`nextCursor` de la page précédente) ; tri : conversations épinglées par ce
 *   membre en tête, puis dernier message décroissant.
 * Seuls les dossiers qui ont au moins un message apparaissent.
 */
export const InboxQuery = z.strictObject({
  scope: z.enum(['open', 'all']).default('open'),
  unread: queryBool.optional(),
  toHandle: queryBool.optional(),
  mine: queryBool.optional(),
  pinned: queryBool.optional(),
  q: z.string().trim().max(100).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(30),
});

export const InboxItem = z.object({
  interventionId: Id,
  status: InterventionStatus,
  plate: z.string(),
  vehicle: z.object({ make: z.string(), model: z.string() }),
  customerName: z.string(),
  /** Mécanicien assigné au dossier, ou `null`. */
  mechanic: z.object({ membershipId: Id, name: z.string() }).nullable(),
  lastMessage: z.object({
    id: Id,
    /**
     * Début du message en texte brut (`INBOX_EXCERPT_LENGTH` caractères au plus, sans saut de ligne) ;
     * vague 6 : pour un événement, sa phrase rendue (côté garage).
     */
    excerpt: z.string(),
    authorType: MessageAuthorType,
    /** Vague 6 : un événement du dossier n'est jamais un non-lu ni « à traiter ». */
    kind: MessageKind.optional(),
    /** Nom du membre pour un message du garage ; `null` pour le client (affiché « Client »). */
    authorName: z.string().nullable(),
    createdAt: Timestamp,
    mediaCount: z.int().nonnegative(),
  }),
  /** Messages du client postérieurs à la dernière lecture de ce membre (0 pour une session support). */
  unreadCount: z.int().nonnegative(),
  /** Messages du client non traités (`handledAt` nul). */
  toHandle: z.int().nonnegative(),
  /** 03/10/2026 : dossier épinglé par ce membre (`false` pour une session support). */
  pinned: z.boolean(),
});
export type InboxItem = z.infer<typeof InboxItem>;

export const InboxPage = z.object({ items: z.array(InboxItem), nextCursor: z.string().nullable() });
export type InboxPage = z.infer<typeof InboxPage>;

/** Compteur léger du menu : messages non lus et conversations concernées (dossiers ouverts). */
export const InboxUnreadCount = z.object({ unread: z.int().nonnegative(), conversations: z.int().nonnegative() });

// ─── Notifications push (PWA) ───────────────────────────────────────────────

const Base64Url = z.string().regex(/^[A-Za-z0-9_-]+={0,2}$/, 'Base64 URL attendu');

/**
 * Abonnement Web Push du navigateur (`PushSubscription.toJSON()`), rattaché au **membre du garage
 * actif**. L'URL doit être en HTTPS et appartenir à un service de push connu (`PUSH_SERVICE_HOSTS`) :
 * le serveur n'envoie jamais de requête vers une adresse choisie librement (SSRF).
 */
export const PushSubscribeInput = z.strictObject({
  endpoint: z.url({ protocol: /^https$/ }).max(2048),
  keys: z.strictObject({ p256dh: Base64Url.max(200), auth: Base64Url.max(100) }),
  /** Libellé de l'appareil choisi par l'utilisateur (« Téléphone atelier »), facultatif. */
  device: z.string().trim().max(100).optional(),
});
export const PushUnsubscribeInput = z.strictObject({ endpoint: z.url({ protocol: /^https$/ }).max(2048) });

/**
 * Suffixes d'hôtes des services de push des navigateurs (Chrome/Edge/Android, Firefox, Safari, Windows).
 * Ce ne sont pas des domaines du produit : ce sont les seules destinations possibles d'un envoi push.
 */
export const PUSH_SERVICE_HOSTS: readonly string[] = [
  'fcm.googleapis.com',
  'android.googleapis.com',
  'updates.push.services.mozilla.com',
  'push.services.mozilla.com',
  'web.push.apple.com',
  'notify.windows.com',
];

export function isAllowedPushEndpoint(endpoint: string): boolean {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || url.username || url.password || (url.port && url.port !== '443')) return false;
  const host = url.hostname.toLowerCase();
  return PUSH_SERVICE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
}

/**
 * Corps fixe d'une notification « Décision reçue » (council n°2, priorité 1) : jamais de nom, de plaque
 * ni de montant ; seulement la nature de la décision.
 */
export const DECISION_PUSH_BODIES: Record<DecisionValue, string> = {
  accepted: 'Travaux complémentaires acceptés — ouvrir le dossier',
  refused: 'Travaux complémentaires refusés — ouvrir le dossier',
  postponed: 'Travaux complémentaires reportés — ouvrir le dossier',
  change_requested: 'Modification demandée sur des travaux complémentaires — ouvrir le dossier',
};
export const DECISION_PUSH_TITLE = 'Décision reçue';

/**
 * Charge utile d'une notification push : **aucune donnée personnelle** (ni plaque, ni nom, ni texte du
 * message, ni montant), seulement un titre (et un corps) fixes, l'identifiant opaque de l'intervention et
 * celui de l'appartenance destinataire (`membershipId`, pour ouvrir le dossier dans le bon garage).
 * - `message.customer` : nouveau message d'un client → conversation ;
 * - `complement.decided` (02/10/2026) : décision reçue sur des travaux complémentaires → onglet
 *   « Travaux complémentaires » du dossier.
 */
/**
 * Chiffre de la pastille de l'application installée (03/10/2026) : éléments à traiter par le
 * **destinataire** (non-lus + décisions à appliquer), calculé par le serveur. Entier borné, non personnel.
 */
export const AppBadge = z.int().min(0).max(APP_BADGE_MAX);

export const PushPayload = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('message.customer'),
    title: z.literal('Nouveau message d’un client'),
    interventionId: Id,
    membershipId: Id.optional(),
    badge: AppBadge.optional(),
  }),
  z.strictObject({
    type: z.literal('complement.decided'),
    title: z.literal(DECISION_PUSH_TITLE),
    body: z.enum(Object.values(DECISION_PUSH_BODIES) as [string, ...string[]]),
    decision: DecisionValue,
    interventionId: Id,
    membershipId: Id.optional(),
    badge: AppBadge.optional(),
  }),
]);
export type PushPayload = z.infer<typeof PushPayload>;

/** Charge utile « Décision reçue » pour une décision (texte fixe, sans donnée personnelle). */
export function decisionPushPayload(decision: DecisionValue, interventionId: string): PushPayload {
  return { type: 'complement.decided', title: DECISION_PUSH_TITLE, body: DECISION_PUSH_BODIES[decision], decision, interventionId };
}

// ─── Application installable (PWA) ──────────────────────────────────────────

/**
 * Icônes de l'application installée : 180 px (`apple-touch-icon`, iPhone), 192 et 512 px, et une
 * version « maskable » 512 px (zone de sécurité). Générées depuis le logo du garage actif, ou ses
 * initiales ; manifeste et icônes génériques de la plateforme sans session de membre.
 */
export const PWA_ICON_VARIANTS = ['180', '192', '512', 'maskable'] as const;
export const PwaIconParams = z.strictObject({ variant: z.enum(PWA_ICON_VARIANTS) });

/**
 * Manifeste web (`app.manifest`, `application/manifest+json`). Par garage pour un membre connecté de ce
 * garage (nom, couleur, `start_url` `/g/<slug>/`) ; sinon le manifeste générique de la plateforme.
 * Aucune donnée personnelle ; jamais de garage désigné par l'URL (aucune énumération possible).
 */
export const WebManifest = z.object({
  id: z.string(),
  name: z.string(),
  short_name: z.string().max(12),
  lang: z.literal('fr'),
  start_url: z.string(),
  scope: z.literal('/'),
  display: z.literal('standalone'),
  theme_color: z.string(),
  background_color: z.string(),
  icons: z.array(
    z.object({ src: z.string(), sizes: z.string(), type: z.literal('image/png'), purpose: z.enum(['any', 'maskable']) }),
  ),
});
export type WebManifest = z.infer<typeof WebManifest>;

// ─── Lecture de plaque ──────────────────────────────────────────────────────

/** Image envoyée à `vehicles.plateScan` : déjà recadrée par l'utilisateur (cadre de visée). */
export const PLATE_SCAN_LIMITS = {
  maxBytes: 2 * 1024 * 1024,
  contentTypes: ['image/jpeg', 'image/webp'] as readonly string[],
  /** Délai maximal de lecture côté serveur (ms) : au-delà, 503 et saisie manuelle. */
  timeoutMs: 3000,
  /** Appels par minute et par membre. */
  perMinutePerMember: 240,
} as const;

/** Champs texte du formulaire multipart (l'image est la partie `file`). */
export const PlateScanFields = z.strictObject({});

/**
 * Résultat : `plate` est la valeur normalisée par `normalizePlate('fr', …)` du meilleur candidat
 * valide, ou `null`. `candidates` : lectures brutes compactes (au plus 3), à afficher pour correction.
 */
export const PlateScanOutput = z.object({
  plate: z.string().nullable(),
  plateKind: z.literal('fr').nullable(),
  confidence: z.number().min(0).max(1),
  candidates: z.array(z.string().max(20)).max(3),
});
export type PlateScanOutput = z.infer<typeof PlateScanOutput>;
